// URL-state filters for the jobs page, ported 1:1 from ionq-cloud-ui
// (src/components/Jobs/useJobsFilters.ts + src/hooks/useUrlFilters.ts): same
// param names, same validation/sanitization, same round-trip encoding — a
// filtered URL copied between the legacy app and the shell keeps meaning the
// same thing. Only the last hop differs: instead of serializing to the
// /v0.3 query string, the applied filters map onto the structured
// trpc jobs.list input (mapUiFiltersToListFilters).

import type { JOB_DB_STATUSES, JobsListFilters } from '@ionq/trpc-console';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { getFriendlyTargetName } from './targets';

export const DATE_RANGE_SELECTOR_OPTIONS = [
    'equals',
    'before',
    'after',
    'between',
] as const;

// The legacy dropdown's display order; `satisfies` locks every entry to the
// server's status enum so the two lists can't drift apart silently.
export const JOB_STATUSES = [
    'completed',
    'canceled',
    'failed',
    'running',
    'submitted',
    'ready',
] as const satisfies readonly (typeof JOB_DB_STATUSES)[number][];

// The legacy page hardcodes the backend filter choices.
export const BACKEND_FILTER_OPTIONS = [
    'simulator',
    'qpu.forte-enterprise-1',
    'qpu.forte-1',
    'qpu.aria-2',
    'qpu.aria-1',
    'harmony',
] as const;

export type DateRangeSelector = (typeof DATE_RANGE_SELECTOR_OPTIONS)[number];
type JobStatus = (typeof JOB_STATUSES)[number];

export interface SelectOption {
    value: string;
    label: string;
}

export interface UiFilters {
    name?: string;
    id?: string;
    projectId?: string;
    backend?: SelectOption[];
    status?: SelectOption[];
    dateRangeSelector?: DateRangeSelector | '';
    filterDate?: string;
    filterEndDate?: string;
    metadataKey?: string;
    metadataValue?: string;
}

// URL params the jobs page owns (and may therefore clear).
export const MY_JOBS_FILTER_NAMES = [
    'id',
    'name',
    'date',
    'filterEndDate',
    'filterDate',
    'status',
    'backend',
    'projectId',
    'metadataKey',
    'metadataValue',
];

// Validated to the CONTRACT (z.uuid), not the legacy shape: the donor's
// permissive [a-zA-Z0-9-_]* fed the public API, which answered a non-uuid
// with zero rows — here it would be a tRPC 400 that throwOnError escalates
// into the route error boundary. An invalid id now just drops the filter.
const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const getValidId = (id?: string): string =>
    typeof id === 'string' && UUID_RE.test(id) ? id : '';

const getValidName = (name?: string): string => {
    if (typeof name !== 'string') return '';
    const sanitized = name.replace(/[^\w\s.,;:!?()-]/g, '');
    return sanitized.trim();
};

// Calendar-valid, matching the contract's z.iso.date() (zod v4 rejects
// impossible dates like 2025-02-31; a shape-only regex would 400 server-side).
const getValidDate = (date?: string): string => {
    if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return '';
    }
    const [year = 0, month = 0, day = 0] = date.split('-').map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day
        ? date
        : '';
};

const getValidDateRangeSelector = (
    selector?: string,
): DateRangeSelector | '' =>
    (DATE_RANGE_SELECTOR_OPTIONS as readonly (string | undefined)[]).includes(
        selector,
    )
        ? (selector as DateRangeSelector)
        : '';

const getValidStatus = (status?: string): SelectOption[] => {
    if (!status) return [];
    return status
        .split(',')
        .map((s) => s.trim())
        .filter((s): s is JobStatus =>
            (JOB_STATUSES as readonly string[]).includes(s),
        )
        .map((s) => ({ value: s, label: s }));
};

const getValidBackend = (backend?: string): SelectOption[] => {
    if (!backend) return [];
    return backend
        .split(',')
        .map((b) => b.trim())
        .filter((b) => /^[a-zA-Z0-9.\-_]+$/.test(b))
        .map((b) => ({ value: b, label: getFriendlyTargetName(b) }));
};

const getValidMetadata = (value?: string): string =>
    typeof value === 'string' ? value.replace(/[<>]/g, '') : '';

const getValidDateFilters = (
    date: string[] | null,
): Pick<UiFilters, 'dateRangeSelector' | 'filterDate' | 'filterEndDate'> => {
    if (!date || date.length < 2)
        return { dateRangeSelector: '', filterDate: '', filterEndDate: '' };
    const dateRangeSelector = getValidDateRangeSelector(date[0]);
    const filterDate = dateRangeSelector
        ? getValidDate(date[1]?.split('|')[0])
        : '';
    const filterEndDate =
        dateRangeSelector === 'between'
            ? getValidDate(date[1]?.split('|')[1])
            : '';
    if (!filterDate || (dateRangeSelector === 'between' && !filterEndDate)) {
        return { dateRangeSelector: '', filterDate: '', filterEndDate: '' };
    }
    return { dateRangeSelector, filterDate, filterEndDate };
};

export const getFiltersFromSearchParams = (
    searchParams: URLSearchParams,
    filterNames: string[],
): UiFilters => {
    const urlParamMap = filterNames.reduce<Record<string, string>>(
        (acc, param) => {
            acc[param] = searchParams.get(param) || '';
            return acc;
        },
        {},
    );

    const date = urlParamMap.date?.split('=') || null;
    const { dateRangeSelector, filterDate, filterEndDate } =
        getValidDateFilters(date);

    return {
        name: getValidName(urlParamMap.name),
        id: getValidId(urlParamMap.id),
        projectId: getValidId(urlParamMap.projectId),
        backend: getValidBackend(urlParamMap.backend),
        status: getValidStatus(urlParamMap.status),
        dateRangeSelector,
        filterDate,
        filterEndDate,
        metadataKey: getValidMetadata(urlParamMap.metadataKey),
        // A value without a key applies no predicate — treating it as active
        // would light the Filter button over an unfiltered result.
        metadataValue: urlParamMap.metadataKey
            ? getValidMetadata(urlParamMap.metadataValue)
            : '',
    };
};

export const hasActiveFilters = (filters: UiFilters): boolean =>
    Object.values(filters).some((value) =>
        Array.isArray(value) ? value.length > 0 : Boolean(value),
    );

export const filtersToSearchParams = (filters: UiFilters): URLSearchParams => {
    const params = new URLSearchParams();
    const dateFields = new Set([
        'dateRangeSelector',
        'filterDate',
        'filterEndDate',
    ]);

    for (const [key, value] of Object.entries(filters)) {
        if (!value || dateFields.has(key)) continue;
        if (Array.isArray(value)) {
            if (value.length === 0) continue;
            params.set(key, value.map((item) => item.value).join(','));
        } else {
            params.set(key, value);
        }
    }

    if (filters.dateRangeSelector && filters.filterDate) {
        let dateValue = `${filters.dateRangeSelector}=${filters.filterDate}`;
        if (filters.dateRangeSelector === 'between' && filters.filterEndDate) {
            dateValue += `|${filters.filterEndDate}`;
        }
        params.set('date', dateValue);
    }

    return params;
};

// The structured twin of the legacy mapUiJobsFiltersToApiFilters: same
// inclusion rules (empty selections dropped, metadata value defaults to ''),
// but the BFF input keeps lists as arrays instead of comma-joining. The
// return type is the SERVER contract type, so it can't drift from the
// jobs.list input schema.
export const mapUiFiltersToListFilters = (
    uiFilters: UiFilters,
): JobsListFilters => {
    const filters: JobsListFilters = {};
    if (uiFilters.id) filters.id = uiFilters.id;
    if (uiFilters.projectId) filters.projectId = uiFilters.projectId;
    if (uiFilters.name) filters.name = uiFilters.name;
    if (uiFilters.status?.length) {
        filters.status = uiFilters.status.map((option) => option.value);
    }
    if (uiFilters.backend?.length) {
        filters.target = uiFilters.backend.map((option) => option.value);
    }
    if (uiFilters.metadataKey) {
        filters.metadata = {
            key: uiFilters.metadataKey,
            value: uiFilters.metadataValue || '',
        };
    }
    if (
        uiFilters.dateRangeSelector &&
        uiFilters.filterDate &&
        // The contract rejects between-without-endDate (upstream 400s it);
        // never send a half-built range.
        (uiFilters.dateRangeSelector !== 'between' || uiFilters.filterEndDate)
    ) {
        filters.date = {
            selector: uiFilters.dateRangeSelector,
            date: uiFilters.filterDate,
            ...(uiFilters.dateRangeSelector === 'between' &&
            uiFilters.filterEndDate
                ? { endDate: uiFilters.filterEndDate }
                : {}),
        };
    }
    return filters;
};

/** Jobs filters as URL state (delete-then-reapply over owned params only). */
export const useUrlFilters = (
    filterNames: string[],
): {
    filters: UiFilters;
    setFilters: (newFilters: UiFilters) => void;
} => {
    const [searchParams, setSearchParams] = useSearchParams();

    const namesKey = filterNames.join(',');
    // `date` is unioned in because filtersToSearchParams encodes
    // filterDate/filterEndDate into the single composite param.
    const ownedNames = useMemo(
        () => [...new Set([...(namesKey ? namesKey.split(',') : []), 'date'])],
        [namesKey],
    );

    const filters = useMemo(
        () => getFiltersFromSearchParams(searchParams, ownedNames),
        [searchParams, ownedNames],
    );

    const setFilters = useCallback(
        (newFilters: UiFilters) => {
            setSearchParams(
                (params) => {
                    // Deleting first is load-bearing for Clear; params the
                    // page does not own survive untouched.
                    for (const name of ownedNames) params.delete(name);
                    filtersToSearchParams(newFilters).forEach((value, key) => {
                        params.set(key, value);
                    });
                    return params;
                },
                // Filter churn must not fill the back button.
                { replace: true, preventScrollReset: true },
            );
        },
        [setSearchParams, ownedNames],
    );

    return { filters, setFilters };
};
